import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BrandLogo, Button, Stack, TextInput } from '@fluent/ui';
import { supabase } from '../lib/supabase.js';
import { supabaseAuthErrorKey } from '../auth/errors.js';
import './auth.css';

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e: typeof errors = {};
    if (!email) e.email = t('auth.errors.email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t('auth.errors.invalid_email');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (error) {
      setErrors({ form: t(supabaseAuthErrorKey(error)) });
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <main className="auth-screen">
        <div className="auth-card">
          <BrandLogo className="auth-card__brand" />
          <h1 className="auth-card__title">{t('auth.forgot.sent_title')}</h1>
          <p className="auth-card__subtitle">{t('auth.forgot.sent_body')}</p>
          <Link to="/login" className="auth-card__alt">
            <Button variant="neutral" size="lg" fullWidth>
              {t('common.back')}
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <form className="auth-card" onSubmit={submit} noValidate>
        <BrandLogo className="auth-card__brand" />
        <h1 className="auth-card__title">{t('auth.forgot.title')}</h1>
        <p className="auth-card__subtitle">{t('auth.forgot.subtitle')}</p>

        <Stack gap={4}>
          <TextInput
            label={t('auth.forgot.email_label')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />
          {errors.form && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', fontWeight: 700 }}>
              {errors.form}
            </span>
          )}
          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
            {t('auth.forgot.submit')}
          </Button>
        </Stack>

        <p className="auth-card__alt">
          <Link to="/login">{t('common.back')}</Link>
        </p>
      </form>
    </main>
  );
}
